import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  Checkbox,
  Grid,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  Avatar
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { getDesignations, bulkUploadDesignations } from "../../../../../api/designationApi";
import { getEmployees } from "../../../../../api/employeeApi";
import { useNavigate } from "react-router-dom";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PermissionGuard from "../../../../../components/PermissionGuard";
import BulkUploadModal from '../../../../../components/common/BulkUploadModal';

const getInitials = (name) => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  const initials = parts.slice(0, 2).map((p) => p[0]?.toUpperCase()).join('');
  return initials || 'D';
};

const filterInputStyle = {
  borderRadius: '10px',
  backgroundColor: '#fff',
  transition: 'all 0.2s ease-in-out',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(226, 232, 240, 0.8)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(117, 34, 209, 0.4)',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#7522D1',
  },
};

const stringToColor = (string) => {
  if (!string) return '#7522D1';
  let hash = 0;
  for (let i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = '#';
  for (let i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
};

export default function Designation() {
  const [designations, setDesignations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedDesignationId, setSelectedDesignationId] = useState(null);
  const [tab, setTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [employeesLoading, setEmployeesLoading] = useState(false);

  // Notification State
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  // Menu State
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchDesignations();
  }, []);

  const fetchDesignations = async () => {
    try {
      setLoading(true);
      const res = await getDesignations();
      if (res.success) {
        setDesignations(res.data);
        if (res.data.length > 0 && !selectedDesignationId) {
          setSelectedDesignationId(res.data[0].id);
        }
      }
    } catch (error) {
      console.error("Error fetching designations:", error);
      showNotification("Failed to fetch designations", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedDesignationId && tab === 1) {
      fetchEmployees();
    }
  }, [selectedDesignationId, tab]);

  const fetchEmployees = async () => {
    try {
      setEmployeesLoading(true);
      const res = await getEmployees({ designationId: selectedDesignationId });
      if (res.success) {
        setEmployees(res.data);
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
      showNotification("Failed to fetch employees", "error");
    } finally {
      setEmployeesLoading(false);
    }
  };

  const showNotification = (message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  };

  const handleCloseNotification = (event, reason) => {
    if (reason === 'clickaway') return;
    setNotification({ ...notification, open: false });
  };

  const handleMenuClick = (event, designation) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedDesignationId(designation.id);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleEdit = () => {
    if (selectedDesignation) {
      navigate(`/org-management/org-structure/edit-designation/${selectedDesignation.id}`, {
        state: { designationData: selectedDesignation }
      });
    }
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const filteredDesignations = useMemo(() => {
    return designations.filter((d) => d.name.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [searchQuery, designations]);

  const filteredEmployees = useMemo(() => {
    return employees.filter((e) => {
      const fullName = `${e.firstName || ''} ${e.lastName || ''}`.toLowerCase();
      const empId = String(e.employeeId || '').toLowerCase();
      const search = employeeSearchQuery.toLowerCase();
      return fullName.includes(search) || empId.includes(search);
    });
  }, [employees, employeeSearchQuery]);

  const handleBulkUpload = async (formData) => {
    const result = await bulkUploadDesignations(formData);
    if (result.success) {
      showNotification(result.message, 'success');
      fetchDesignations();
      setIsUploadOpen(false);
    } else {
      throw new Error(result.message || 'Upload failed');
    }
  };

  const selectedDesignation = useMemo(() => {
    return designations.find((d) => d.id === selectedDesignationId) ?? null;
  }, [selectedDesignationId, designations]);

  return (
    <Box sx={{ width: '100%', marginTop: 0 }}>
      {/* Header Card */}
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          padding: '20px 24px',
          marginBottom: 3,
          borderRadius: '12px',
          border: '1px solid rgba(117, 34, 209, 0.08)',
          borderLeft: '6px solid #7522D1',
          boxShadow: '0 4px 20px rgba(117, 34, 209, 0.04)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: '#030007',
              marginBottom: 0.5,
              fontSize: '1.1rem',
            }}
          >
            Designations
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#4F4937',
              fontSize: '0.85rem',
            }}
          >
            Manage your organization's designations
          </Typography>
        </Box>
        <Box sx={{ display: 'flex' }}>
          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            onClick={() => setIsUploadOpen(true)}
            sx={{
              borderColor: 'rgba(117, 34, 209, 0.5)',
              color: '#7522D1',
              textTransform: 'none',
              fontWeight: 600,
              paddingX: 2,
              paddingY: 1,
              borderRadius: '10px',
              mr: 2,
              whiteSpace: 'nowrap',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                borderColor: '#7522D1',
                backgroundColor: 'rgba(117, 34, 209, 0.04)',
                transform: 'translateY(-1px)',
              },
            }}
          >
            Upload Excel
          </Button>
          <PermissionGuard module="designation" action="create">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/org-management/org-structure/add-designation')}
              sx={{
                backgroundColor: '#7522D1',
                color: '#FFFFFF',
                textTransform: 'none',
                fontWeight: 600,
                paddingX: 2.5,
                paddingY: 1.2,
                borderRadius: '10px',
                whiteSpace: 'nowrap',
                boxShadow: '0 4px 12px rgba(117, 34, 209, 0.2)',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: '#5E1BA8',
                  boxShadow: '0 6px 16px rgba(117, 34, 209, 0.3)',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              Add Designation
            </Button>
          </PermissionGuard>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress sx={{ color: '#7522D1' }} />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
          {/* Sidebar */}
          <Box sx={{ width: { xs: '100%', md: '30%' }, minWidth: { xs: '100%', md: 300 } }}>
            <TextField
              fullWidth
              placeholder="Search here"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ marginRight: 1, color: '#7522D1', fontSize: '1.2rem' }} />,
              }}
              variant="outlined"
              sx={{
                marginBottom: 2,
                '& .MuiOutlinedInput-root': filterInputStyle,
              }}
            />

            <Typography
              sx={{
                fontWeight: 600,
                color: '#030007',
                marginBottom: 2,
                fontSize: '0.9rem',
              }}
            >
              Designations ({filteredDesignations.length})
            </Typography>

            {filteredDesignations.map((d) => (
              <Card
                key={d.id}
                onClick={() => setSelectedDesignationId(d.id)}
                sx={{
                  padding: 2,
                  marginBottom: 1.5,
                  cursor: 'pointer',
                  backgroundColor: selectedDesignationId === d.id ? '#F5EEFC' : '#FFFFFF',
                  border: selectedDesignationId === d.id ? '1px solid #7522D1' : '1px solid rgba(117, 34, 209, 0.08)',
                  borderLeft: selectedDesignationId === d.id ? '6px solid #7522D1' : '1px solid rgba(117, 34, 209, 0.08)',
                  borderRadius: '12px',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: selectedDesignationId === d.id ? '0 4px 12px rgba(117, 34, 209, 0.08)' : 'none',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(117, 34, 209, 0.12)',
                    borderColor: '#7522D1',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '10px',
                      backgroundColor: stringToColor(d.name),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      color: '#FFFFFF',
                      fontWeight: 700,
                      fontSize: '0.9rem',
                    }}
                  >
                    {getInitials(d.name)}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: '#030007',
                        fontSize: '0.95rem',
                      }}
                    >
                      {d.name}
                    </Typography>
                    <Typography
                      sx={{
                        color: '#4F4937',
                        fontSize: '0.85rem',
                      }}
                    >
                      {d.employeeCount || 0} Employees
                    </Typography>
                  </Box>
                  <IconButton onClick={(e) => handleMenuClick(e, d)} size="small">
                    <MoreVertIcon sx={{ color: '#4F4937' }} />
                  </IconButton>
                </Box>
              </Card>
            ))}
          </Box>

          {/* Main Content */}
          <Box sx={{ flex: 1 }}>
            {selectedDesignation ? (
              <Card
                sx={{
                  padding: 4,
                  boxShadow: '0 4px 20px rgba(117, 34, 209, 0.02)',
                  border: '1px solid rgba(117, 34, 209, 0.08)',
                  borderRadius: '16px',
                  backgroundColor: '#FFFFFF',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '12px',
                        backgroundColor: stringToColor(selectedDesignation.name),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#FFFFFF',
                        fontWeight: 700,
                        fontSize: '1.2rem',
                      }}
                    >
                      {getInitials(selectedDesignation.name)}
                    </Avatar>
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 650,
                          color: '#030007',
                          fontSize: '1.1rem',
                        }}
                      >
                        {selectedDesignation.name}
                      </Typography>
                      <Typography
                        sx={{
                          color: '#4F4937',
                          fontSize: '0.9rem',
                          fontWeight: 500,
                        }}
                      >
                        {selectedDesignation.employeeCount || 0} Employees
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Tabs
                  value={tab}
                  onChange={(_, v) => setTab(v)}
                  sx={{
                    borderBottom: '1px solid rgba(117, 34, 209, 0.08)',
                    marginBottom: 3,
                    '& .MuiTabs-indicator': {
                      backgroundColor: '#7522D1',
                      height: '3px',
                      borderRadius: '3px 3px 0 0',
                    },
                  }}
                >
                  <Tab
                    label="Summary"
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.95rem',
                      color: '#4F4937',
                      fontWeight: tab === 0 ? 600 : 400,
                      '&.Mui-selected': {
                        color: '#7522D1',
                      },
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        color: '#7522D1',
                      },
                    }}
                  />
                  <Tab
                    label="Employees"
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.95rem',
                      color: '#4F4937',
                      fontWeight: tab === 1 ? 600 : 400,
                      '&.Mui-selected': {
                        color: '#7522D1',
                      },
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        color: '#7522D1',
                      },
                    }}
                  />
                </Tabs>

                {/* Tab Content */}
                {tab === 0 && (
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: '#030007',
                        marginBottom: 1,
                        fontSize: '0.9rem',
                      }}
                    >
                      Description
                    </Typography>
                    <Typography
                      sx={{
                        color: '#4F4937',
                        fontSize: '0.9rem',
                        marginBottom: 3,
                      }}
                    >
                      {selectedDesignation.description || 'No description available for this designation.'}
                    </Typography>
                  </Box>
                )}
                {tab === 1 && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <TextField
                        size="small"
                        placeholder="Search here"
                        value={employeeSearchQuery}
                        onChange={(e) => setEmployeeSearchQuery(e.target.value)}
                        InputProps={{
                          startAdornment: <SearchIcon sx={{ marginRight: 1, color: '#7522D1', fontSize: '1.1rem' }} />,
                        }}
                        variant="outlined"
                        sx={{
                          width: 250,
                          '& .MuiOutlinedInput-root': filterInputStyle,
                        }}
                      />
                      <Box sx={{ flex: 1 }} />
                    </Box>
                    <TableContainer
                      component={Paper}
                      elevation={0}
                      sx={{
                        border: '1px solid rgba(117, 34, 209, 0.08)',
                        borderRadius: '12px',
                        overflow: 'hidden',
                      }}
                    >
                      {employeesLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                          <CircularProgress size={24} sx={{ color: '#7522D1' }} />
                        </Box>
                      ) : (
                        <Table size="medium">
                          <TableHead sx={{ backgroundColor: '#F8F5FC' }}>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, color: '#42374F', fontSize: '0.85rem', paddingY: 1.5 }}>EMPLOYEE NUMBER</TableCell>
                              <TableCell sx={{ fontWeight: 600, color: '#42374F', fontSize: '0.85rem', paddingY: 1.5 }}>EMPLOYEE NAME</TableCell>
                              <TableCell sx={{ fontWeight: 600, color: '#42374F', fontSize: '0.85rem', paddingY: 1.5 }}>JOB TITLE</TableCell>
                              <TableCell sx={{ fontWeight: 600, color: '#42374F', fontSize: '0.85rem', paddingY: 1.5 }}>REPORTING TO</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredEmployees.map((e) => (
                              <TableRow
                                key={e.id}
                                sx={{
                                  '&:hover': { backgroundColor: 'rgba(117, 34, 209, 0.02)' },
                                  transition: 'all 0.15s ease-in-out',
                                }}
                              >
                                <TableCell sx={{ paddingY: 1.5, color: '#030007', fontWeight: 500 }}>{e.employeeId}</TableCell>
                                <TableCell sx={{ paddingY: 1.5 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Avatar
                                      sx={{
                                        width: 32,
                                        height: 32,
                                        fontSize: '0.85rem',
                                        fontWeight: 600,
                                        backgroundColor: stringToColor(`${e.firstName} ${e.lastName}`),
                                        color: '#FFFFFF',
                                      }}
                                    >
                                      {getInitials(`${e.firstName} ${e.lastName}`)}
                                    </Avatar>
                                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 500, color: '#030007' }}>
                                      {`${e.firstName} ${e.lastName}`}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ paddingY: 1.5, color: '#4F4937' }}>{e.designation?.name || selectedDesignation.name}</TableCell>
                                <TableCell sx={{ paddingY: 1.5, color: '#4F4937' }}>
                                  {e.manager ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Avatar
                                        sx={{
                                          width: 24,
                                          height: 24,
                                          fontSize: '0.75rem',
                                          fontWeight: 600,
                                          backgroundColor: stringToColor(`${e.manager.firstName} ${e.manager.lastName}`),
                                          color: '#FFFFFF',
                                        }}
                                      >
                                        {getInitials(`${e.manager.firstName} ${e.manager.lastName}`)}
                                      </Avatar>
                                      <Typography sx={{ fontSize: '0.85rem', color: '#4F4937' }}>
                                        {`${e.manager.firstName} ${e.manager.lastName}`}
                                      </Typography>
                                    </Box>
                                  ) : 'N/A'}
                                </TableCell>
                              </TableRow>
                            ))}
                            {filteredEmployees.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={4} align="center" sx={{ py: 3, color: '#4F4937' }}>
                                  No employees found
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      )}
                    </TableContainer>
                  </Box>
                )}
                {tab === 2 && (
                  <Box>
                    {[1, 2].map((_, idx) => (
                      <Card key={idx} elevation={0} sx={{ mb: 3, p: 2, border: '1px solid rgba(117, 34, 209, 0.08)', borderRadius: '12px' }}>
                        <Typography sx={{ fontWeight: 600, color: '#030007', marginBottom: 1, fontSize: '0.9rem' }}>
                          Entity Details
                        </Typography>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Checkbox checked sx={{ p: 0, pr: 1, color: '#7522D1', '&.Mui-checked': { color: '#7522D1' } }} readOnly />
                            <Typography sx={{ color: '#4F4937', fontSize: '0.9rem' }}>
                              I can provide a business document or email confirmation if needed.
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Checkbox checked sx={{ p: 0, pr: 1, color: '#7522D1', '&.Mui-checked': { color: '#7522D1' } }} readOnly />
                            <Typography sx={{ color: '#4F4937', fontSize: '0.9rem' }}>
                              I can provide a business document.
                            </Typography>
                          </Box>
                        </Box>
                      </Card>
                    ))}
                  </Box>
                )}
              </Card>
            ) : (
              <Card
                sx={{
                  padding: 4,
                  boxShadow: '0 4px 20px rgba(117, 34, 209, 0.02)',
                  border: '1px solid rgba(117, 34, 209, 0.08)',
                  borderRadius: '16px',
                  textAlign: 'center',
                  backgroundColor: '#FFFFFF',
                }}
              >
                <Typography color="text.secondary">Select a designation to view details</Typography>
              </Card>
            )}
          </Box>
        </Box>
      )}

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{
            width: '100%',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 1.5,
            minWidth: 160,
            borderRadius: '12px',
            border: '1px solid rgba(117, 34, 209, 0.08)',
            boxShadow: '0 10px 30px rgba(117, 34, 209, 0.08)',
            overflow: 'visible',
            '& .MuiMenuItem-root': {
              fontSize: '0.9rem',
              padding: '10px 16px',
              color: '#42374F',
              borderRadius: '8px',
              margin: '4px 8px',
              transition: 'all 0.15s ease-in-out',
              '&:hover': {
                backgroundColor: 'rgba(117, 34, 209, 0.05)',
                color: '#7522D1',
              },
            },
          },
        }}
      >
        <PermissionGuard module="designation" action="update">
          <MenuItem onClick={handleEdit}>
            <EditIcon sx={{ mr: 1.5, fontSize: 18 }} />
            Edit
          </MenuItem>
        </PermissionGuard>
        <PermissionGuard module="designation" action="delete">
          <MenuItem onClick={handleDeleteClick}>
            <DeleteIcon sx={{ mr: 1.5, fontSize: 18 }} />
            Delete
          </MenuItem>
        </PermissionGuard>
      </Menu>

      <BulkUploadModal
        open={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        title="Upload Designations"
        onUpload={handleBulkUpload}
      />
    </Box>
  );
}